import { useState, useEffect } from "react";
import { Button } from "@component/button";
import { Label } from "@component/label";
import { ArrowRightIcon, ArrowLeftIcon, CheckIcon } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { Badge } from "@component/badge";
import { cn } from "@util/tailwind";
import { Checkbox } from "@component/checkbox";

interface EmployerDetailsFormProps {
    onSubmit: (data: EmployerDetailsData) => void;
    onBack: () => void;
    className?: string;
    initialData?: Partial<EmployerDetailsData>;
}

export interface EmployerDetailsData {
    hiringCount: string;
    employeeTypes: string[];
    organizationType: string;
    otherOrganizationType?: string;
}

export default function EmployerDetailsForm({
    onSubmit,
    onBack,
    className,
    initialData = {},
}: EmployerDetailsFormProps) {
    const [formData, setFormData] = useState<EmployerDetailsData>({
        hiringCount: initialData.hiringCount || "",
        employeeTypes: initialData.employeeTypes || [],
        organizationType: initialData.organizationType || "",
    });

    // Update form data when initialData changes
    useEffect(() => {
        setFormData({
            hiringCount: initialData.hiringCount || formData.hiringCount,
            employeeTypes: initialData.employeeTypes || formData.employeeTypes,
            organizationType: initialData.organizationType || formData.organizationType,
        });
    }, [initialData]);

    const handleSelectChange = (name: string, value: string) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleEmployeeTypeToggle = (type: string) => {
        setFormData((prev) => {
            const currentTypes = prev.employeeTypes || [];
            const newTypes = currentTypes.includes(type)
                ? currentTypes.filter((t) => t !== type)
                : [...currentTypes, type];

            return { ...prev, employeeTypes: newTypes };
        });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    const hiringCountOptions = ["1-10", "10-50", "50-100", "100-500", "500+"];

    const employeeTypes = [
        "Contract/Contingent Workers",
        "Salaried Employees (Remote)",
        "Salaried Employees (In-person)",
    ];

    const organizationTypes = [
        "AI Company",
        "Technology Company",
        "Staffing Agency/Talent Acquisition",
        "Consultancy/Managed Service Provider",
        "Capital Allocator",
        "Multi-national Corporation",
        "Other",
    ];

    const isFormComplete =
        formData.hiringCount &&
        formData.employeeTypes.length > 0 &&
        formData.organizationType;

    return (
        <form
            onSubmit={handleSubmit}
            className={cn("space-y-6", className)}
            data-pol-id="2pnoti"
            data-pol-file-name="employer-details-form"
            data-pol-file-type="component"
        >
            <div
                className="space-y-4"
                data-pol-id="k52xdu"
                data-pol-file-name="employer-details-form"
                data-pol-file-type="component"
            >
                <h3
                    className="text-lg font-medium"
                    data-pol-id="p186l3"
                    data-pol-file-name="employer-details-form"
                    data-pol-file-type="component"
                >
                    Employer Details
                </h3>

                <div
                    className="space-y-2"
                    data-pol-id="hqtx90"
                    data-pol-file-name="employer-details-form"
                    data-pol-file-type="component"
                >
                    <Label
                        htmlFor="hiringCount"
                        data-pol-id="qaypap"
                        data-pol-file-name="employer-details-form"
                        data-pol-file-type="component"
                    >
                        How many people are you interested in hiring?
                    </Label>
                    <Select
                        value={formData.hiringCount}
                        onValueChange={(value) => handleSelectChange("hiringCount", value)}
                        required
                        data-pol-id="p2z5ed"
                        data-pol-file-name="employer-details-form"
                        data-pol-file-type="component"
                    >
                        <SelectTrigger
                            data-pol-id="5788ih"
                            data-pol-file-name="employer-details-form"
                            data-pol-file-type="component"
                        >
                            <SelectValue
                                placeholder="Select hiring count range"
                                data-pol-id="n2qqk5"
                                data-pol-file-name="employer-details-form"
                                data-pol-file-type="component"
                            />
                        </SelectTrigger>
                        <SelectContent
                            data-pol-id="f81f9p"
                            data-pol-file-name="employer-details-form"
                            data-pol-file-type="component"
                        >
                            {hiringCountOptions.map((option, index) => (
                                <SelectItem
                                    key={option}
                                    value={option}
                                    data-pol-id={`7c6jrc_${index}`}
                                    data-pol-file-name="employer-details-form"
                                    data-pol-file-type="component"
                                >
                                    {option}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div
                    className="space-y-2"
                    data-pol-id="7xtq9w"
                    data-pol-file-name="employer-details-form"
                    data-pol-file-type="component"
                >
                    <Label
                        data-pol-id="etqlnr"
                        data-pol-file-name="employer-details-form"
                        data-pol-file-type="component"
                    >
                        Which of the following are you looking to hire? (Select all that
                        apply)
                    </Label>
                    <div
                        className="space-y-2 border rounded-md p-3"
                        data-pol-id="htx45a"
                        data-pol-file-name="employer-details-form"
                        data-pol-file-type="component"
                    >
                        {employeeTypes.map((type, index) => (
                            <div
                                key={type}
                                className="flex items-center space-x-2"
                                data-pol-id={`wa7265_${index}`}
                                data-pol-file-name="employer-details-form"
                                data-pol-file-type="component"
                            >
                                <Checkbox
                                    id={`employee-type-${index}`}
                                    checked={formData.employeeTypes.includes(type)}
                                    onCheckedChange={() => handleEmployeeTypeToggle(type)}
                                    data-pol-id={`zf2026_${index}`}
                                    data-pol-file-name="employer-details-form"
                                    data-pol-file-type="component"
                                />

                                <Label
                                    htmlFor={`employee-type-${index}`}
                                    className="cursor-pointer"
                                    data-pol-id={`dpo0wo_${index}`}
                                    data-pol-file-name="employer-details-form"
                                    data-pol-file-type="component"
                                >
                                    {type}
                                </Label>
                            </div>
                        ))}
                    </div>

                    {formData.employeeTypes.length > 0 && (
                        <div
                            className="flex flex-wrap gap-1 mt-2"
                            data-pol-id="cqajx8"
                            data-pol-file-name="employer-details-form"
                            data-pol-file-type="component"
                        >
                            {formData.employeeTypes.map((type, index) => (
                                <Badge
                                    key={type}
                                    variant="secondary"
                                    className="flex items-center gap-1"
                                    data-pol-id={`1rsv5t_${index}`}
                                    data-pol-file-name="employer-details-form"
                                    data-pol-file-type="component"
                                >
                                    {type}
                                    <button
                                        type="button"
                                        className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                        onClick={() => handleEmployeeTypeToggle(type)}
                                        data-pol-id={`o9v1jm_${index}`}
                                        data-pol-file-name="employer-details-form"
                                        data-pol-file-type="component"
                                    >
                                        <span
                                            className="sr-only"
                                            data-pol-id={`qfmn1m_${index}`}
                                            data-pol-file-name="employer-details-form"
                                            data-pol-file-type="component"
                                        >
                                            Remove
                                        </span>
                                        <CheckIcon
                                            className="h-3 w-3 text-[#3118DC]"
                                            data-pol-id={`ehp2gs_${index}`}
                                            data-pol-file-name="employer-details-form"
                                            data-pol-file-type="component"
                                        />
                                    </button>
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>

                <div
                    className="space-y-2"
                    data-pol-id="c3nhpp"
                    data-pol-file-name="employer-details-form"
                    data-pol-file-type="component"
                >
                    <Label
                        htmlFor="organizationType"
                        data-pol-id="qfc3pb"
                        data-pol-file-name="employer-details-form"
                        data-pol-file-type="component"
                    >
                        Which of the following best describes your organization?
                    </Label>
                    <Select
                        value={formData.organizationType}
                        onValueChange={(value) =>
                            handleSelectChange("organizationType", value)
                        }
                        required
                        data-pol-id="u0eiwf"
                        data-pol-file-name="employer-details-form"
                        data-pol-file-type="component"
                    >
                        <SelectTrigger
                            data-pol-id="k0j7o1"
                            data-pol-file-name="employer-details-form"
                            data-pol-file-type="component"
                        >
                            <SelectValue
                                placeholder="Select organization type"
                                data-pol-id="m3w8h3"
                                data-pol-file-name="employer-details-form"
                                data-pol-file-type="component"
                            />
                        </SelectTrigger>
                        <SelectContent
                            data-pol-id="kxtsea"
                            data-pol-file-name="employer-details-form"
                            data-pol-file-type="component"
                        >
                            {organizationTypes.map((type, index) => (
                                <SelectItem
                                    key={type}
                                    value={type}
                                    data-pol-id={`vt1jxc_${index}`}
                                    data-pol-file-name="employer-details-form"
                                    data-pol-file-type="component"
                                >
                                    {type}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div
                className="flex flex-col sm:flex-row gap-3 pt-2"
                data-pol-id="ikw5bv"
                data-pol-file-name="employer-details-form"
                data-pol-file-type="component"
            >
                <Button
                    type="button"
                    variant="outline"
                    className="sm:flex-1"
                    onClick={onBack}
                    data-pol-id="2xc5v5"
                    data-pol-file-name="employer-details-form"
                    data-pol-file-type="component"
                >
                    <ArrowLeftIcon
                        className="mr-2 h-4 w-4"
                        data-pol-id="rlav7c"
                        data-pol-file-name="employer-details-form"
                        data-pol-file-type="component"
                    />
                    Back
                </Button>
                <Button
                    type="submit"
                    className="bg-blue-700 hover:bg-blue-800 text-white sm:flex-1"
                    disabled={!isFormComplete}
                    data-pol-id="u6dnso"
                    data-pol-file-name="employer-details-form"
                    data-pol-file-type="component"
                >
                    Continue
                    <ArrowRightIcon
                        className="ml-2 h-4 w-4"
                        data-pol-id="svezbh"
                        data-pol-file-name="employer-details-form"
                        data-pol-file-type="component"
                    />
                </Button>
            </div>
        </form>
    );
}
